import { SORT_DIRECTION, SortDirection } from "baseui/table";
import { atom } from "recoil";
import { selector } from "./recoilSelectors";
import { durationMillis, TrackEntry, TrackOrStopEntry } from "../types/track";
import {
    retrieveStopsFromGeojson,
    retrieveTrackEntriesFromGeojson,
    retrieveTrackEntriesOrStopsFromGeojson
} from "../utils/geojsonUtils";
import { dateMinusDays } from "../utils/time";
import {
    allSelectedTracksLayerState,
    reviewTracksLayerState,
    tracksForLocationTableSelectedMembersState
} from "./geojsonState";
import { atomWithPersistence } from "../utils/persistence";
import { Uid } from "../types/core";

export const hoveredTrackState = atom<TrackEntry | undefined>({
    key: 'tracks.hovered',
    default: undefined,
});

export const tracksSelectedUidsState = atom<Uid[]>({
    key: "tracks.selected.uids",
    default: [],
});

export const trackOrStopEntriesState = selector<TrackOrStopEntry[]>({
    key: "tracks.entries",
    get: ({ get }) => {
        const layers = get(tracksForLocationTableSelectedMembersState);
        const entries: TrackOrStopEntry[] = [];
        const tracks = layers.map((layer) => retrieveTrackEntriesOrStopsFromGeojson(layer)).flat();
        entries.push(...tracks);
        const sort = get(tracksSortState);
        const sortDirection = get(tracksSortDirectionState);
        entries.sort((e1, e2) => {
            if (sort === TrackSort.DURATION) {
                return durationMillis(e1) - durationMillis(e2);
            }
            if (sort === TrackSort.DISTANCE) {
                return e1.distance - e2.distance;
            }
            if (sort === TrackSort.NAME) {
                return e2.account.localeCompare(e1.account);
            }
            if (sort === TrackSort.APPROVAL) {
                return (e1.approvalStatus || 0) - (e2.approvalStatus || 0);
            }
            return (e1.startTs || e1.endTs) - (e2.startTs || e2.endTs);
        })
        return sortDirection === SORT_DIRECTION.DESC ? entries.reverse() : entries;
    }
});

/** Location page shows the company-wide pending-review tracks instead of the selected members' tracks. */
export const reviewScopeActiveState = atom({
    key: "tracks.review.active",
    default: false,
});

/** Tracks of the company-wide review scope (member=all), newest first as delivered by the server. */
export const reviewTrackEntriesState = selector<TrackOrStopEntry[]>({
    key: "tracks.review.entries",
    get: ({ get }) => {
        const layer = get(reviewTracksLayerState);
        return retrieveTrackEntriesOrStopsFromGeojson(layer).filter(entry => entry.kind === "track");
    }
});

export const selectedTrackEntriesState = selector<TrackEntry[]>({
    key: 'tracks.entries.selected',
    get: ({ get }) => {
        const selectedTracksLayer = get(allSelectedTracksLayerState);
        return selectedTracksLayer.map((layer) => retrieveTrackEntriesFromGeojson(layer)).flat();
    }
});

export const selectedStopEntriesState = selector<TrackEntry[]>({
    key: 'tracks.stops.selected',
    get: ({ get }) => {
        const selectedTracksLayer = get(allSelectedTracksLayerState);
        return selectedTracksLayer.map((layer) => retrieveStopsFromGeojson(layer)).flat();
    }
});

export const selectedTrackTimeSliderProgress = atom({ key: "tracks.entries.selected.slider.progress", default: 0 });

export enum TrackSort {
    DATE,
    DURATION,
    DISTANCE,
    NAME,
    APPROVAL,
}

export const tracksSortState = atom({
    key: "tracks.sort",
    default: TrackSort.DATE,
});

export const tracksSortDirectionState = atom<SortDirection>({
    key: "tracks.sort.direction",
    default: SORT_DIRECTION.ASC,
});

export const tracksDatesState = atom<(Date | null | undefined)[]>({
    key: "tracks.dates",
    default: [dateMinusDays(new Date(), 6), new Date()],
});

export const isMultiTracksDatesState = atomWithPersistence("tracks.dates.ismulti", false);

export const isTracksAnimatingState = atom({
    key: "tracks.animation",
    default: false,
});

/** Playback speed multiplier: 0.2, 1 or 4. */
export const trackPlaybackSpeedState = atom<number>({
    key: "tracks.animation.speed",
    default: 1,
});